import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
} from '@mui/material';
import NumberRangeQuestion from '../../questions/numberRange/NumberRangeQuestion';
import {
  DigitalQuestion,
  DigitalSubmission,
  DigitalSubmissionChoiceWithText,
  DigitalSubmissionMultiChoice,
  DigitalSubmissionNumberRange,
  DigitalSubmissionRatio,
  DigitalSubmissionSingleChoice,
  DigitalSubmissionString,
  PartialBy,
  QuestionCaseSetting,
  QuestionTypeV2,
} from '../../../redux/types';
import MultiChoiceQuestion from '../../questions/multiChoice/MultiChoiceQuestion';
import SingleChoiceQuestion from '../../questions/singleChoice/SingleChoiceQuestion';
import { DigitalSubmissionRequest } from '../../../loopback/apiClient/DRTApi';
import ApiClient from '../../../loopback/apiClient';
import StringQuestion from '../../questions/string/StringQuestion';
import RatioQuestion from '../../questions/ratio/RatioQuestion';
import SingleChoiceWithTextQuestion from '../../questions/singleChoiceWithText/SingleChoiceWithTextQuestion';
import MultiChoiceWithTextQuestion from '../../questions/multiChoiceWithText/MultiChoiceWithTextQuestion';
import { QuestionSaveOp } from '../../../hooks/survey/useQuestionSaveStatus';

type Props = {
  trialId: number;
  caseId: number;
  sectionId: number;
  enrollmentId: number;
  questions: DigitalQuestion[];
  submissionsMapByQuestionAndCaseId: Record<number, Record<number, DigitalSubmission> | undefined>;
  disabled?: boolean;
  addOrUpdateDigitalSubmission: (submission: DigitalSubmission) => void;
  hilightedQuestionId?: number;
  setHilightedQuestion?: React.Dispatch<React.SetStateAction<number | undefined>>,
  settingsDictionary: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
  getSaveStatus?: (op: QuestionSaveOp, qId: number, caseId?: number) => void;
  showErrorNotification?: (msg: string) => void;
  manualSave?: boolean;
  saveOnBlur?: boolean;
};

const QuestionInputForm: React.FC<Props> = ({
  trialId,
  caseId,
  sectionId,
  enrollmentId,
  questions,
  submissionsMapByQuestionAndCaseId,
  disabled,
  settingsDictionary,
  addOrUpdateDigitalSubmission,
  hilightedQuestionId: hQId,
  setHilightedQuestion,
  getSaveStatus,
  showErrorNotification,
  manualSave,
  saveOnBlur,
}: Props) => {
  const anchorEl = useRef<HTMLElement>();
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    // remove highlight effect in after timeout
    if (setHilightedQuestion && hQId !== undefined) {
      timeout = setTimeout(() => {
        setHilightedQuestion(undefined);
      }, 1250);
    }
    // scroll to question DOM
    if (anchorEl.current && hQId) {
      anchorEl.current.scrollIntoView({ behavior: 'smooth' });
      anchorEl.current = undefined;
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [setHilightedQuestion, hQId]);

  const onSaveHandler = useCallback(async (val?: PartialBy<DigitalSubmission, 'id'>) => {
    if (val === undefined) {
      return;
    }
    const reqPayload: DigitalSubmissionRequest = {
      answer: val.answer,
      digitalQuestionId: val.digitalQuestionId,
      digitalSectionId: val.digitalSectionId,
      digitalCaseId: val.digitalCaseId,
      type: val.type,
      trialId,
    };
    try {
      const savedSubmission = await ApiClient.addOrUpdateDigitalSubmission(reqPayload);
      addOrUpdateDigitalSubmission(savedSubmission);
    } catch (e) {
      if (showErrorNotification) {
        showErrorNotification(`Error saving digital submission ${e}`);
      }
    }
  }, [addOrUpdateDigitalSubmission, showErrorNotification, trialId]);

  return (
    <Box sx={{ p: '1.5rem' }}>
      {
        questions
          .filter((q) => !settingsDictionary.get(q.id, caseId)?.hide)
          .map((q) => (
            <Box
              key={q.id}
              ref={q.id === hQId ? anchorEl : undefined}
              id={`${q.id}`}
              sx={{
                transition: 'background-color 0.5s ease-in-out',
                backgroundColor: q.id === hQId ? '#eff58c' : '',
                mx: '-10px',
                px: '10px',
              }}
            >
              <Box sx={{ py: '1.5rem' }}>
                {
                  q.type === QuestionTypeV2.NumberRange && (
                    <NumberRangeQuestion
                      mode="submit"
                      caseId={caseId}
                      sectionId={sectionId}
                      enrollmentId={enrollmentId}
                      question={q}
                      onSave={onSaveHandler}
                      defaultSubmission={
                        submissionsMapByQuestionAndCaseId[q.id]
                          ?.[caseId] as DigitalSubmissionNumberRange
                      }
                      disabled={disabled}
                      getSaveStatus={getSaveStatus}
                      manualSave={manualSave}
                      saveOnBlur={saveOnBlur}
                    />
                  )
                }
                {
                  // @deprecated
                  q.type === QuestionTypeV2.SingleChoice && (
                    <SingleChoiceQuestion
                      mode="submit"
                      caseId={caseId}
                      sectionId={sectionId}
                      enrollmentId={enrollmentId}
                      question={q}
                      onSave={onSaveHandler}
                      defaultSubmission={submissionsMapByQuestionAndCaseId[q.id]
                        ?.[caseId] as DigitalSubmissionSingleChoice}
                      disabled={disabled}
                    />
                  )
                }
                {
                  // @deprecated
                  q.type === QuestionTypeV2.MultiChoice && (
                    <MultiChoiceQuestion
                      mode="submit"
                      caseId={caseId}
                      sectionId={sectionId}
                      enrollmentId={enrollmentId}
                      question={q}
                      onSave={onSaveHandler}
                      defaultSubmission={submissionsMapByQuestionAndCaseId[q.id]
                        ?.[caseId] as DigitalSubmissionMultiChoice}
                      disabled={disabled}
                    />
                  )
                }
                {
                  q.type === QuestionTypeV2.String && (
                    <StringQuestion
                      mode="submit"
                      caseId={caseId}
                      sectionId={sectionId}
                      enrollmentId={enrollmentId}
                      question={q}
                      onSave={onSaveHandler}
                      defaultSubmission={submissionsMapByQuestionAndCaseId[q.id]
                        ?.[caseId] as DigitalSubmissionString}
                      disabled={disabled}
                      getSaveStatus={getSaveStatus}
                      manualSave={manualSave}
                      saveOnBlur={saveOnBlur}
                    />
                  )
                }
                {
                  q.type === QuestionTypeV2.Ratio && (
                    <RatioQuestion
                      mode="submit"
                      caseId={caseId}
                      sectionId={sectionId}
                      enrollmentId={enrollmentId}
                      question={q}
                      onSave={onSaveHandler}
                      defaultSubmission={submissionsMapByQuestionAndCaseId[q.id]
                        ?.[caseId] as DigitalSubmissionRatio}
                      disabled={disabled}
                      getSaveStatus={getSaveStatus}
                      manualSave={manualSave}
                      saveOnBlur={saveOnBlur}
                    />
                  )
                }
                {
                  q.type === QuestionTypeV2.SingleChoiceWithText && (
                    <SingleChoiceWithTextQuestion
                      mode="submit"
                      caseId={caseId}
                      sectionId={sectionId}
                      enrollmentId={enrollmentId}
                      question={q}
                      onSave={onSaveHandler}
                      defaultSubmission={submissionsMapByQuestionAndCaseId[q.id]
                        ?.[caseId] as
                        DigitalSubmissionChoiceWithText<QuestionTypeV2.SingleChoiceWithText>}
                      disabled={disabled}
                      getSaveStatus={getSaveStatus}
                      manualSave={manualSave}
                      saveOnBlur={saveOnBlur}
                    />
                  )
                }
                {
                  q.type === QuestionTypeV2.MultiChoiceWithText && (
                    <MultiChoiceWithTextQuestion
                      mode="submit"
                      caseId={caseId}
                      sectionId={sectionId}
                      enrollmentId={enrollmentId}
                      question={q}
                      onSave={onSaveHandler}
                      defaultSubmission={submissionsMapByQuestionAndCaseId[q.id]
                        ?.[caseId] as
                        DigitalSubmissionChoiceWithText<QuestionTypeV2.MultiChoiceWithText>}
                      disabled={disabled}
                      getSaveStatus={getSaveStatus}
                      manualSave={manualSave}
                      saveOnBlur={saveOnBlur}
                    />
                  )
                }
              </Box>
            </Box>
          ))
      }
    </Box>
  );
};

export default QuestionInputForm;
